import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { darkPageCanvasBackgroundClassName } from "@/lib/page-canvas";
import { cn } from "@/lib/utils";
import {
  createPublicThread,
  getThreadAttachmentProcessingStatus,
  listPublicLibraries,
  pollThreadAttachmentUntilDone,
  updateThreadLibraries,
  uploadThreadAttachment,
} from "@/apps/rag/api/rag-api.js";
import { mapApiProcessingStatus, mapPublicLibrary } from "@/apps/rag/api/mappers.js";
import { DEFAULT_CHAT_TITLE } from "@/apps/rag/constants.js";
import { AddSourcesDialog } from "@/apps/rag/components/AddSourcesDialog.js";
import { ChatComposer, type AttachedDoc } from "@/apps/rag/components/ChatComposer.js";
import { useRefreshRagNav } from "@/apps/rag/hooks/RagNavRefreshContext.js";
import { useAttachedDocPolling } from "@/apps/rag/hooks/useAttachedDocPolling.js";
import { useAgenticPreference } from "@/apps/rag/hooks/useAgenticPreference.js";
import type { ApiProcessingStatus } from "@/apps/rag/api/types.js";
import type { RagLibrary } from "@/apps/rag/types.js";
import { regulatoryGlobalChatPath } from "../paths.js";

export function RegulatoryGlobalChatListPage() {
  const navigate = useNavigate();
  const refreshRagNav = useRefreshRagNav();
  const [libraries, setLibraries] = useState<RagLibrary[]>([]);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const agenticPreference = useAgenticPreference();
  const [creating, setCreating] = useState(false);
  const [preparingAttach, setPreparingAttach] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [draftThreadId, setDraftThreadId] = useState<string | null>(null);
  const [attachedDocs, setAttachedDocs] = useState<AttachedDoc[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const draftThreadIdRef = useRef<string | null>(null);
  const ensuringDraftRef = useRef<Promise<string> | null>(null);
  const selectedLibraryIdsRef = useRef<string[]>([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const { libraries: libraryData } = await listPublicLibraries();
        const mappedLibraries = libraryData.map(mapPublicLibrary);
        const libraryIds = mappedLibraries.map((library) => library.id);
        setLibraries(mappedLibraries);
        selectedLibraryIdsRef.current = libraryIds;
        setSelectedLibraryIds(libraryIds);
      } catch {
        setLibraries([]);
        selectedLibraryIdsRef.current = [];
        setSelectedLibraryIds([]);
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, []);

  const ensureDraftThread = useCallback(async (): Promise<string> => {
    if (draftThreadIdRef.current) return draftThreadIdRef.current;
    if (ensuringDraftRef.current) return ensuringDraftRef.current;

    ensuringDraftRef.current = (async () => {
      const libraryIds = selectedLibraryIdsRef.current;
      const { thread } = await createPublicThread({
        title: DEFAULT_CHAT_TITLE,
        libraryIds,
      });
      draftThreadIdRef.current = thread.id;
      setDraftThreadId(thread.id);
      const latestIds = selectedLibraryIdsRef.current;
      const selectionChanged =
        latestIds.length !== libraryIds.length ||
        latestIds.some((id) => !libraryIds.includes(id));
      if (selectionChanged) {
        await updateThreadLibraries(thread.id, latestIds);
      }
      refreshRagNav();
      return thread.id;
    })();

    try {
      return await ensuringDraftRef.current;
    } finally {
      ensuringDraftRef.current = null;
    }
  }, [refreshRagNav]);

  async function handleLibraryIdsChange(nextIds: string[]) {
    if (nextIds.length === 0) return;

    const previousIds = selectedLibraryIdsRef.current;
    selectedLibraryIdsRef.current = nextIds;
    setSelectedLibraryIds(nextIds);

    const threadId = draftThreadIdRef.current;
    if (!threadId) return;

    try {
      await updateThreadLibraries(threadId, nextIds);
    } catch {
      selectedLibraryIdsRef.current = previousIds;
      setSelectedLibraryIds(previousIds);
    }
  }

  const pollAttachment = useCallback(
    (
      attachmentId: string,
      onStatus?: (status: ApiProcessingStatus | null | undefined) => void,
    ) => {
      const threadId = draftThreadIdRef.current;
      if (!threadId) {
        return Promise.reject(new Error("No chat thread for attachment"));
      }
      return pollThreadAttachmentUntilDone(threadId, attachmentId, { onStatus });
    },
    [],
  );

  const syncAttachmentStatus = useCallback((attachmentId: string) => {
    const threadId = draftThreadIdRef.current;
    if (!threadId) {
      return Promise.resolve(null);
    }
    return getThreadAttachmentProcessingStatus(threadId, attachmentId);
  }, []);

  const { startPolling: startPollingAttachment } = useAttachedDocPolling({
    items: attachedDocs,
    setItems: setAttachedDocs,
    enabled: Boolean(draftThreadId),
    poll: pollAttachment,
    syncStatus: syncAttachmentStatus,
  });

  const handleUpload = useCallback(
    async (file: File, title?: string) => {
      const threadId = await ensureDraftThread();
      const { attachment } = await uploadThreadAttachment(threadId, file, title);
      const status = mapApiProcessingStatus(attachment.processingStatus);

      setAttachedDocs((prev) => [
        ...prev,
        { id: attachment.id, name: attachment.title, status },
      ]);

      startPollingAttachment(attachment.id, status);
    },
    [ensureDraftThread, startPollingAttachment],
  );

  async function handleOpenAttach() {
    if (creating || preparingAttach) return;

    setPreparingAttach(true);
    setCreateError(null);
    try {
      await ensureDraftThread();
      setUploadOpen(true);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : "Failed to prepare chat for attachments");
    } finally {
      setPreparingAttach(false);
    }
  }

  async function handleStartChat(message: string) {
    if (creating) return;

    setCreating(true);
    setCreateError(null);
    try {
      const threadId = draftThreadId ?? (await ensureDraftThread());
      refreshRagNav();
      navigate(regulatoryGlobalChatPath(threadId), {
        state: { initialMessage: message },
      });
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : "Failed to create chat");
    } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <section className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </section>
    );
  }

  const libraryCountLabel =
    libraries.length === 1 ? "1 library" : `${libraries.length} libraries`;
  const selectedCount = selectedLibraryIds.length;
  const scopeLabel =
    selectedCount === 0
      ? "No libraries in scope"
      : `${selectedCount} ${selectedCount === 1 ? "library" : "libraries"} selected`;

  return (
    <section
      className={cn(
        "relative flex min-h-[calc(100dvh-3rem)] flex-1 flex-col items-center justify-center overflow-hidden px-4 py-8 sm:px-6",
        darkPageCanvasBackgroundClassName,
      )}
    >
      <div className="relative z-10 flex w-full max-w-3xl flex-col items-center gap-8 text-center">
        <div className="space-y-2">
          <h1 className="text-2xl font-normal tracking-tight text-[var(--lm-foreground-strong)] sm:text-3xl dark:text-white">
            Where should we start?
          </h1>
          <p className="text-sm text-[var(--lm-muted)] dark:text-slate-400">
            Regulatory · {libraryCountLabel}
          </p>
        </div>
        {createError ? (
          <p className="w-full rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-left text-sm text-red-200">
            {createError}
          </p>
        ) : null}
        <div className="w-full">
          <ChatComposer
            placeholder="Ask across selected public libraries…"
            scopeVariant="workspace"
            showLibraryScopeBar
            libraryScope={libraries.map((library) => ({
              id: library.id,
              name: library.name,
              selected: selectedLibraryIds.includes(library.id),
            }))}
            onLibraryIdsChange={(ids) => void handleLibraryIdsChange(ids)}
            scopeLabel={scopeLabel}
            attachedDocs={attachedDocs}
            onRemoveDoc={(docId) =>
              setAttachedDocs((prev) => prev.filter((doc) => doc.id !== docId))
            }
            onAttachDocs={() => void handleOpenAttach()}
            onSubmit={handleStartChat}
            disabled={creating || preparingAttach}
            agenticAvailable={agenticPreference.available}
            agenticEnabled={agenticPreference.agentic}
            onAgenticChange={agenticPreference.setAgentic}
            className="border-0 bg-transparent p-0 backdrop-blur-none dark:border-0 dark:bg-transparent"
          />
        </div>
      </div>
      <AddSourcesDialog open={uploadOpen} onOpenChange={setUploadOpen} onUpload={handleUpload} />
    </section>
  );
}
